<template>
  <div>
    <div class="wrapper">
      <breadcrumb :crumbs="[$t('menu.cashs'), $t('text.list')]"> </breadcrumb>
    </div>
    <narrow
      :narrows="narrows"
      :init-filter="initFilter"
      @narrowSelect="narrowSelect"
    />
    <div class="search with-narrows">
      <el-form
        :inline="true"
        @submit.native.prevent="search"
        :model="searchForm"
        size="small"
      >
        <el-form-item>
          <el-input
            v-model.trim="searchForm.keyword"
            :placeholder="$t('material.serialNoName')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-search"
            native-type="submit"
          />
        </el-form-item>
      </el-form>
    </div>
    <div class="wrapper table-area">
      <sticky :sticky-top="50" :viewport="1024" :is-rem="false" class="sticky">
        <vxe-toolbar ref="recordsToolbar" custom zoom class="table-toolbar">
          <template v-slot:buttons>
            <el-button type="primary" size="mini" @click="batchCashReStore">
              {{ $t("action.cashReStore") }}
            </el-button>

            <el-dropdown class="mgl10" @command="download">
              <el-button type="default" size="mini">
                {{ $t("action.download") }}
                <i class="el-icon-caret-bottom el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="checked">
                  {{ $t("action.downLoadChecked") }}
                </el-dropdown-item>
                <el-dropdown-item command="search">
                  {{ $t("action.downLoadSearch") }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>

            <span class="pager-total">
              {{ $t("rules.pagerTotal", [pager.total]) }}
            </span>
          </template>
        </vxe-toolbar>
      </sticky>

      <vxe-table
        :data="records"
        @checkbox-change="setSelected"
        @checkbox-all="setSelected"
        :custom-config="{ storage: true, checkMethod: checkColumnMethod }"
        :loading="loading"
        id="materialTable"
        row-id="id"
        ref="recordsTable"
        class="recordsTable"
        size="mini"
        resizable
        round
        auto-resize
        highlight-hover-row
        :scroll-y="{ enabled: false }"
      >
        <vxe-table-column field="checkbox" type="checkbox" width="35" />
        <vxe-table-column
          field="material.image"
          :title="$t('product.images')"
          width="80"
        >
          <template v-slot="{ row }">
            <thumbnail :images="row.material.image" />
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="materialSerialNo"
          :title="$t('material.serialNo')"
          min-width="100"
        >
          <template v-slot="{ row }">
            {{ row.materialSerialNo }}
            <div class="font10">{{ row.material.name }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="material.subjectName"
          :title="$t('material.subject')"
          min-width="80"
        />
        <vxe-table-column
          field="material.color"
          :title="$t('material.color')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.material.color }}

            <div class="font10">{{ row.material.colorNo }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="cashQuantity"
          :title="$t('material.cashQuantity')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <el-popover
              placement="right"
              width="600"
              trigger="click"
              @show="getStockInOuts(row)"
            >
              <vxe-table
                :data="stockInOuts"
                id="stockInOutsTable"
                row-id="id"
                ref="stockInOutsTable"
                class="recordsTable"
                size="mini"
                resizable
                round
                auto-resize
                highlight-hover-row
              >
                <vxe-table-column field="stockType" min-width="40">
                  <template v-slot="{ row }">
                    <span v-if="row.stockType == 'in'" class="green">入</span>
                    <span v-if="row.stockType == 'out'" class="red">出</span>
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="quantity"
                  :title="$t('text.quantity')"
                  min-width="80"
                >
                  <template v-slot="{ row }">
                    <span v-if="row.unit == row.inUnit">
                      {{ row.quantity }}{{ row.unit }}
                    </span>
                    <span v-else>
                      {{ row.quantity }}{{ row.unit }} = {{ row.inQuantity
                      }}{{ row.inUnit }}
                    </span>
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="remainQuantity"
                  :title="$t('inventory.remainQuantity')"
                  min-width="80"
                >
                  <template v-slot="{ row }">
                    <span v-if="row.unit == row.inUnit">
                      {{ row.remainQuantity }}{{ row.unit }}
                    </span>
                    <span v-else>
                      {{ row.quantity }}{{ row.unit }} =
                      {{ row.inRemainQuantity }}{{ row.inUnit }}
                    </span>
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="associateSerialNo"
                  :title="$t('notice.serialNo')"
                  min-width="150"
                />
                <vxe-table-column
                  field="remark"
                  :title="$t('text.remark')"
                  min-width="80"
                >
                  <template v-slot="{ row }">
                    <raw-html-limit-number
                      :content="row.remark"
                      :number="6"
                      type="richText"
                    />
                  </template>
                </vxe-table-column>
                <vxe-table-column
                  field="createdAt"
                  :title="$t('text.createAt')"
                  min-width="110"
                />
              </vxe-table>
              <span slot="reference" class="blue popover-reference font10">
                <span v-if="row.material.unit == row.material.inUnit">
                  {{ row.cashQuantity }}{{ row.material.unit }}
                </span>
                <span v-else>
                  {{ row.cashQuantity }}{{ row.material.unit }} =
                  {{ row.inCashQuantity }}{{ row.material.inUnit }}
                </span>
              </span>
            </el-popover>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="warehouseName"
          :title="$t('menu.warehouses')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.warehouseName }}
            <div class="font10">{{ $t("menu.shelfs") }}：{{ row.shelf }}</div>
          </template>
        </vxe-table-column>
      </vxe-table>
      <div class="flex flex-between pager">
        <div class="selected-qty-tips">
          选中了
          <span class="strong">{{ selectRecordQty }}</span>
          条记录
        </div>
        <div>
          <vxe-pager
            size="small"
            :loading="false"
            :background="false"
            :current-page="pager.currentPage"
            :page-size="pager.pageSize"
            :total="pager.total"
            :layouts="pagerConfig.layouts"
            :page-sizes="pagerConfig.pageSizes"
            @page-change="changePager"
          ></vxe-pager>
        </div>
      </div>
    </div>
    <!-- //退仓 -->
    <el-dialog
      :title="$t('action.cashReStore')"
      :close-on-click-modal="false"
      :visible.sync="batchCashReStoreDF"
      width="60%"
    >
      <el-form
        ref="batchCashReStoreObjForm"
        :model="batchCashReStoreObj"
        label-width="80px"
      >
        <div class="mgb15">
          <vxe-table
            :data="batchCashReStores"
            size="mini"
            resizable
            round
            auto-resize
            highlight-hover-row
            :scroll-y="{ enabled: false }"
          >
            <vxe-table-column
              field="material.image"
              :title="$t('product.images')"
              width="80"
            >
              <template v-slot="{ row }">
                <thumbnail :images="row.material.image" />
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="materialSerialNo"
              :title="$t('material.serialNo')"
              min-width="100"
            >
              <template v-slot="{ row }">
                {{ row.materialSerialNo }}
                <div class="font10">{{ row.material.name }}</div>
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="material.subjectName"
              :title="$t('material.subject')"
              min-width="80"
            />
            <vxe-table-column
              field="material.color"
              :title="$t('material.color')"
              min-width="80"
            >
              <template v-slot="{ row }">
                {{ row.material.color }}

                <div class="font10">{{ row.material.colorNo }}</div>
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="cashQuantity"
              :title="$t('material.cashQuantity')"
              min-width="100"
            >
              <template v-slot="{ row }">
                <span v-if="row.material.unit == row.material.inUnit">
                  {{ row.cashQuantity }}{{ row.material.unit }}
                </span>
                <span v-else>
                  {{ row.cashQuantity }}{{ row.material.unit }} =
                  {{ row.inCashQuantity }}{{ row.material.inUnit }}
                </span>
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="warehouseName"
              :title="$t('menu.warehouses')"
              min-width="80"
            >
              <template v-slot="{ row }">
                {{ row.warehouseName }}
                <div class="font10">
                  {{ $t("menu.shelfs") }}：{{ row.shelf }}
                </div>
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="quantity"
              :title="$t('text.quantity')"
              min-width="60"
            >
              <template v-slot="{ row }">
                <el-input
                  v-model.trim="row._quantity"
                  size="small"
                  :placeholder="row.material.unit"
                >
                  <template slot="append">{{ row.material.unit }}</template>
                </el-input>
              </template>
            </vxe-table-column>
            <vxe-table-column
              field="action"
              :title="$t('text.action')"
              width="50"
            >
              <template v-slot="{ row, rowIndex }">
                <el-button
                  type="text"
                  size="small"
                  @click="remove(row, rowIndex)"
                  >{{ $t("text.remove") }}</el-button
                >
              </template>
            </vxe-table-column>
          </vxe-table>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="batchCashReStoreSubmitDoing"
          size="small"
          @click="batchCashReStoreSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="batchCashReStoreDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import Vue from "vue";
import axios from "axios";
import { page, table } from "@/utils/mixins.js";
import RawHtmlLimitNumber from "@/components/RawHtmlLimitNumber";
import Util from "@/utils/index.js";

import SupplierMms from "@/api/supplierMms.js";
import SubjectMms from "@/api/subjectMms.js";
import InventoryMms from "@/api/inventoryMms.js";
import Warehouse from "@/api/warehouse.js";
import UserWarehouse from "@/api/userWarehouse.js";
import StockInOutMms from "@/api/stockInOutMms.js";

export default {
  mixins: [
    page,
    table.pager,
    table.search,
    table.narrow,
    table.checker,
    table.sorter,
    table.basic,
  ],
  components: {
    RawHtmlLimitNumber,
  },
  data() {
    return {
      Util: Util,

      narrows: [],
      initFilter: null,
      searchForm: {
        serialNo: null,
        supplierId: null,
      },
      sorter: {
        sortBy: "inventories_id",
        sortIndicator: "desc",
      },
      suppliers: [],
      subjects: [],
      warehouses: [],
      userWarehouses: [],
      mmsAuthWarehouses: [],
      //
      row: null,
      currentRowId: null,
      currentRowIdx: null,
      editDF: false,
      // 退仓
      batchCashReStoreDF: false,
      batchCashReStores: [],
      batchCashReStoreObj: {},
      batchCashReStoreSubmitDoing: false,
      //
      stockInOuts: [],
    };
  },

  created() {
    const self = this;
    this.$nextTick(() => {
      // 手动将表格和工具栏进行关联
      this.$refs.recordsTable.connect(this.$refs.recordsToolbar);
    });
    self.loadingStart();
    axios
      .all([
        SubjectMms.findByTenantId(),
        SupplierMms.findByTenantId(),
        Warehouse.findByTenantId(),
        UserWarehouse.findByUserId(self.Permit.currentUser().id),
      ])
      .then(
        axios.spread(function (
          subjects,
          suppliers,
          warehouses,
          userWarehouses
        ) {
          if (subjects.status === 200) {
            if (self.Permit.currentUser().admin) {
              self.subjects = subjects.data;
            } else {
              self.subjects = subjects.data.filter((s) =>
                self.Permit.mmsAuthSubjuctIds().includes(s.id)
              );
            }
            self.narrowsSubjects();
          } else {
            self.sysError();
          }
          if (suppliers.status === 200) {
            self.suppliers = suppliers.data;
          } else {
            self.sysError();
          }
          self.warehouses = warehouses.data;
          self.userWarehouses = userWarehouses.data;
          if (self.Permit.currentUser().admin) {
            self.mmsAuthWarehouses = self.warehouses;
          } else {
            const userWarehouseIds = self.userWarehouses.map(
              (u) => u.warehouseId
            );
            self.mmsAuthWarehouses = self.warehouses.filter((w) =>
              userWarehouseIds.includes(w.id)
            );
          }
          self.narrowsWarehouses();
          self.loadingEnd();
        })
      );
  },
  methods: {
    narrowsSubjects() {
      const self = this;
      if (self.subjects.length != 0) {
        self.narrows.push([
          {
            label: self.$t("material.subject"),
            items: self.subjects.map((subject) => {
              return {
                label: subject.name,
                key: subject.id,
                condition: { subjectId: subject.id },
              };
            }),
          },
        ]);
      }
    },
    narrowsWarehouses() {
      const self = this;
      if (self.mmsAuthWarehouses.length != 0) {
        self.narrows.push([
          {
            label: self.$t("menu.warehouses"),
            items: self.mmsAuthWarehouses.map((warehouse) => {
              return {
                label: warehouse.name,
                key: warehouse.id,
                condition: { warehouseId: warehouse.id },
              };
            }),
          },
        ]);
      }
    },
    getStockInOuts(row) {
      const self = this;
      StockInOutMms.findCashByInventoryId(row.id).then(function (res) {
        if (res.status === 200) {
          self.stockInOuts = res.data;
        } else {
          self.sysError();
        }
      });
    },
    //退仓
    batchCashReStore() {
      const self = this;
      if (self.selectRecords.length === 0) {
        self.$message({
          message: this.$t("prompt.atLeastOneData"),
          type: "warning",
        });
        return;
      }
      self.batchCashReStores = JSON.parse(JSON.stringify(self.selectRecords));
      for (const s of self.batchCashReStores) {
        Vue.set(s, "_quantity", s.cashQuantity);
        Vue.set(s, "_remark", null);
      }
      self.batchCashReStoreSubmitDoing = false;
      self.batchCashReStoreDF = true;
    },
    remove(row, rowIndex) {
      this.batchCashReStores.splice(rowIndex, 1);
    },
    batchCashReStoreSubmit() {
      const self = this;
      if (this.batchCashReStores.length === 0) {
        this.$message.error(this.$t("prompt.atLeastOneData"));
        return;
      }
      let data = [];
      for (const s of self.batchCashReStores) {
        if (!Util.regexQuantity(s._quantity)) {
          self.$message({
            message: self.$t("rules.regex", [self.$t("text.quantity")]),
            type: "warning",
          });
          return;
        }
        data.push({ id: s.id, quantity: s._quantity, remark: s._remark });
      }
      self.batchCashReStoreSubmitDoing = true;
      InventoryMms.batchCashReStore(data).then(function (res) {
        self.batchCashReStoreSubmitDoing = false;
        self.batchCashReStoreDF = false;
        if (res.status == 200) {
          self.sysResults(res.data);
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },

    // 下载
    download(type) {
      const self = this;
      if (type == "checked") {
        if (!self.downloadChecked(self.selectRecords)) {
          return;
        }
        const data = {
          currentPage: 1,
          pageSize: self.limitNumber(),
          ids: self.selectRecords.map((c) => c.id),
        };
        self.downloadData(data);
      } else {
        if (!self.downloadSearch(self.pager.total)) {
          return;
        }
        const data = JSON.parse(JSON.stringify(self.combineParams()));
        data.pageSize = self.limitNumber();
        self.downloadData(data);
      }
    },
    downloadData(data) {
      const self = this;
      InventoryMms.cashSearchExportExcel(data).then(function (res) {
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.downloadPrompt"),
            type: "success",
          });
          self.loadData();
          self.$eventHub.$emit("emitDownLoad");
        } else {
          self.$message.error(self.$t("prompt.downloadErr"));
        }
      });
    },
    //
    dataProvider(params) {
      return InventoryMms.cashSearch(params);
    },
  },
};
</script>
